// Container for the full TradingView-Strategy-Tester-parity Strategy Report. Composes:
//   1. KpiStrip          — top 5 KPI tiles
//   2. EquityChart       — hero equity pane with overlay toggles
//   3. Performance row   — ProfitStructureWaterfall + BenchmarkingChart side-by-side
//   4. ReturnsTable      — All / Long / Short columns
//   5. Benchmark + Risk  — BenchmarkTable + RiskAdjustedTable side-by-side
//   6. Trades analysis   — PnlHistogram + WinLossDonut side-by-side
//   7. TradeList         — enhanced list of trades
//
// This component is pure rendering — no fetching, no state. Driven by a single
// BacktestResponseDto plus the strategy display name (for the header).

import type { BacktestResponseDto, ScanResultDto } from "../../api/types";
import { KpiStrip } from "./KpiStrip";
import { EquityChart } from "./EquityChart";
import { BenchmarkingChart } from "./BenchmarkingChart";
import { ProfitStructureWaterfall } from "./ProfitStructureWaterfall";
import { ReturnsTable } from "./ReturnsTable";
import { BenchmarkTable } from "./BenchmarkTable";
import { RiskAdjustedTable } from "./RiskAdjustedTable";
import { PnlHistogram } from "./PnlHistogram";
import { WinLossDonut } from "./WinLossDonut";
import { TradeList } from "./TradeList";

interface Props {
  result: BacktestResponseDto;
}

export function StrategyReport({ result }: Props) {
  const s = result.strategy;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Mode banner — make it explicit this report is the TradingView-faithful netting
          account (single position, stop-and-reverse, SL/TP/trailing, equity MTM). */}
      {s && (
        <div style={{
          padding: "8px 12px",
          background: "rgba(88, 166, 255, 0.08)",
          border: "1px solid rgba(88, 166, 255, 0.4)",
          borderRadius: 4,
          fontSize: 11,
          color: "#8b949e",
          lineHeight: 1.5,
        }}>
          <strong style={{ color: "#58a6ff" }}>Netting account (TradingView semantics)</strong> ·
          exit mode <code>{s.exitMode}</code> · SL {s.stopLossEnabled ? "on" : "off"} ·
          TP {s.takeProfitEnabled ? "on" : "off"} · trailing {s.trailingEnabled ? "on" : "off"} ·{" "}
          {s.trades} trades ({s.longTrades}L / {s.shortTrades}S · {s.reversals} reversals).
          The single-direction long/short scans are diagnostics — see the bottom of this report.
        </div>
      )}

      {/* Warnings (insufficient data, etc.) */}
      {result.warnings && result.warnings.length > 0 && (
        <ul style={{ color: "#d29922", fontSize: 11, marginTop: 0, marginBottom: 0, paddingLeft: 16 }}>
          {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
        </ul>
      )}

      {/* 1. KPI strip */}
      <KpiStrip result={result} />

      {/* 2. Equity chart */}
      <EquityChart result={result} />

      {/* 3. Performance row: waterfall + benchmarking */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1.5fr", gap: 12 }}>
        <ProfitStructureWaterfall result={result} />
        <BenchmarkingChart result={result} />
      </div>

      {/* 4. Returns table */}
      <ReturnsTable result={result} />

      {/* 5. Benchmark + risk-adjusted side by side */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <BenchmarkTable result={result} />
        <RiskAdjustedTable result={result} />
      </div>

      {/* 6. Trades analysis */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <PnlHistogram result={result} />
        <WinLossDonut result={result} />
      </div>

      {/* 7. Trade list */}
      <TradeList result={result} />

      {/* 8. Single-direction diagnostics — the isolated long/short scans, collapsed by
             default so the netting account stays the focus. */}
      {s && (result.long || result.short) && (
        <details style={{ background: "#0d1117", border: "1px solid #30363d", borderRadius: 6, padding: "8px 12px" }}>
          <summary style={{ cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#c9d1d9" }}>
            Single-direction diagnostics
            <span style={{ color: "#8b949e", fontWeight: 400, fontSize: 11 }}>
              {" "}— isolated long / short scans (not the netting account)
            </span>
          </summary>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 10 }}>
            <ScanDiag label="Long-only scan" scan={result.long} />
            <ScanDiag label="Short-only scan" scan={result.short} />
          </div>
        </details>
      )}
    </div>
  );
}

/** Compact KPI block for one isolated single-direction scan (diagnostics only). */
function ScanDiag({ label, scan }: { label: string; scan: ScanResultDto | null }) {
  if (!scan) {
    return (
      <div style={{ padding: 10, background: "#161b22", borderRadius: 4, fontSize: 11, color: "#8b949e" }}>
        {label}: no trades met the threshold.
      </div>
    );
  }
  const rows: [string, string, string?][] = [
    ["Trades", scan.trades.toLocaleString()],
    ["Win rate", `${scan.winRate.toFixed(2)}%`, scan.winRate >= 50 ? "#3fb950" : "#c9d1d9"],
    ["Total return", `${scan.totalReturn > 0 ? "+" : ""}${scan.totalReturn.toFixed(2)}%`, scan.totalReturn >= 0 ? "#3fb950" : "#f85149"],
    ["Max DD", `${scan.maxStratDD.toFixed(2)}%`, "#f85149"],
    ["Profit factor", scan.profitFactor.toFixed(2), scan.profitFactor >= 1 ? "#3fb950" : "#f85149"],
  ];
  return (
    <div style={{ padding: 10, background: "#161b22", borderRadius: 4 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: "#c9d1d9", marginBottom: 6 }}>{label}</div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <tbody>
          {rows.map(([k, v, c]) => (
            <tr key={k}>
              <td style={{ padding: "3px 0", color: "#8b949e" }}>{k}</td>
              <td style={{ padding: "3px 0", textAlign: "right", color: c ?? "#c9d1d9", fontFamily: "monospace" }}>{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
